<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { debounce } from "lodash-es";
import { useI18n } from "vue-i18n";
import { useRouter, useRoute } from "vue-router";

import { toast } from "@/common/utils/toast";

import {
  methodGroupShow,
  methodGroupUpdate,
} from "@/common/api/rest/method/method-group";

import type { MethodGroupPayloadType } from "@/common/types/method/method-group";

import { RegisterTheme } from "@/common/themes";
import { iconMenu, svgIcon } from "@/common/components";

import MethodGroupForm from "@/components/forms/method-group.vue";

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const methodPayload = ref<MethodGroupPayloadType | null>(null);

const isLoading = ref(false);
const fieldLoading = ref(false);

const accountId = computed(() => String(route.params.id));
const groupId = computed(() => String(route.params.group));

const menuTabs = computed(() => [
  {
    key: "edit",
    icon: "home-line",
    label: t("common.labels.group"),
  },
  {
    key: "field",
    icon: "field",
    label: t("common.labels.fields"),
  },
  {
    key: "method",
    icon: "method",
    label: t("common.labels.methods"),
  },
  {
    key: "question",
    icon: "form",
    label: t("common.labels.questions"),
  },
]);

function back() {
  router.push(`/${accountId.value}/register/method`);
}

function goToTab(tab: string) {
  router.push(`/${accountId.value}/register/method/${groupId.value}/${tab}`);
}

// CATEGORY
function fetchCategory() {
  isLoading.value = true;
  fieldLoading.value = true;

  methodGroupShow(accountId.value, groupId.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        methodPayload.value = {
          status: data.status,
          name: data.name,
          description: data.description,
        };
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      isLoading.value = false;
      fieldLoading.value = false;
    });
}

function updateCategory(payload: MethodGroupPayloadType) {
  methodGroupUpdate(accountId.value, groupId.value, payload)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchCategory();
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

const debounceCategory = debounce(updateCategory, 1000);

onMounted(() => {
  if (groupId.value) {
    fetchCategory();
  }
});
</script>

<template>
  <register-theme tab-active="method">
    <template #title>
      <div class="flex flex-row justify-between items-center w-full">
        <div class="flex flex-row gap-1 items-center">
          <svg-icon icon="method" size="2rem" />
          {{ t("common.labels.edit", { concat: t("common.labels.method") }) }}
        </div>

        <button type="button" class="btn btn-back btn-sm" @click="back()">
          <svg-icon icon="close" size="1.25rem" />
        </button>
      </div>
    </template>

    <template #content>
      <icon-menu :tabs="menuTabs" active="edit" @selected="goToTab($event)">
        <template #content>
          <div class="title">
            {{ t("common.labels.method_group") }}
          </div>

          <method-group-form
            width="100%"
            :data="methodPayload"
            @update-data="debounceCategory($event)"
          />
        </template>
      </icon-menu>
    </template>
  </register-theme>
</template>

<style lang="scss" scoped>
.title {
  display: flex;
  flex-direction: column;
  padding-inline: 0.5rem;
  border-bottom: 1px solid var(--border);
  margin-block: 2rem;
  padding-block-end: 0.5rem;
  font-size: 1.15rem;
  font-weight: bold;
  text-transform: uppercase;
}
</style>
